/**
 * Checks if a store is currently open based on opening_time, closing_time ("HH:MM:SS" or "HH:MM"), and Sunday availability.
 * Returns true if open or if no schedule is provided.
 */
export function isStoreOpen(
  openingTime?: string | null,
  closingTime?: string | null,
  openOnSunday?: boolean | null,
): boolean {
  const now = new Date()
  const isSunday = now.getDay() === 0

  if (isSunday && openOnSunday === false) {
    return false
  }

  if (!openingTime || !closingTime) {
    return true
  }

  const currentMinutes = now.getHours() * 60 + now.getMinutes()

  const parseMinutes = (timeStr: string) => {
    const parts = timeStr.split(':')
    const hours = Number.parseInt(parts[0] || '0', 10)
    const minutes = Number.parseInt(parts[1] || '0', 10)
    return hours * 60 + minutes
  }

  const openMinutes = parseMinutes(openingTime)
  const closeMinutes = parseMinutes(closingTime)

  // When opening and closing times are identical, assume 24h open
  if (openMinutes === closeMinutes) {
    return true
  }

  // Standard daytime operating hours (e.g., 08:00 to 19:00)
  if (closeMinutes > openMinutes) {
    return currentMinutes >= openMinutes && currentMinutes < closeMinutes
  }

  // Overnight operating hours (e.g., 20:00 to 04:00)
  return currentMinutes >= openMinutes || currentMinutes < closeMinutes
}

/**
 * Formats "08:00:00" -> "8:00 AM"
 */
export function formatStoreTime(timeStr?: string | null): string {
  if (!timeStr) return ''
  const parts = timeStr.split(':')
  if (parts.length < 2) return timeStr
  const hours = Number.parseInt(parts[0] || '0', 10)
  const minutes = parts[1] || '00'
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const formattedHours = hours % 12 || 12
  return `${formattedHours}:${minutes} ${ampm}`
}

/**
 * Formats opening and closing time to "8:00 AM - 7:00 PM" (with optional Sunday note)
 */
export function formatOperatingHours(
  openingTime?: string | null,
  closingTime?: string | null,
  openOnSunday?: boolean | null,
): string | null {
  if (!openingTime || !closingTime) return null
  const base = `${formatStoreTime(openingTime)} - ${formatStoreTime(closingTime)}`
  if (openOnSunday === false) {
    return `${base} (Mon – Sat)`
  }
  return base
}

/**
 * Formats numeric distance in miles into a human-readable string (e.g. "1.2 miles", "< 0.1 mile")
 */
export function formatDistance(distanceInMiles?: number | null): string | null {
  if (distanceInMiles === undefined || distanceInMiles === null) return null
  if (distanceInMiles < 0.1 && distanceInMiles > 0) return '< 0.1 mile'
  return `${distanceInMiles.toFixed(1)} ${distanceInMiles === 1 ? 'mile' : 'miles'}`
}

/**
 * Formats city and state/region or a raw location string into a clean, concise display location (e.g. "Stirling, Scotland", "Stirling, UK", or "Manchester")
 */
export function formatStoreLocation(
  city?: string | null,
  stateOrRegion?: string | null,
  fallbackLocation?: string | null,
): string | undefined {
  const parts: string[] = []
  if (city?.trim()) parts.push(city.trim())
  if (stateOrRegion?.trim() && (!city || city.trim().toLowerCase() !== stateOrRegion.trim().toLowerCase())) {
    let region = stateOrRegion.trim()
    if (region.toUpperCase() === 'GB') region = 'UK'
    parts.push(region)
  }
  if (parts.length > 0) return parts.join(', ')
  if (fallbackLocation?.trim()) {
    return fallbackLocation.trim().replace(/,\s*GB$/i, ', UK')
  }
  return undefined
}

/**
 * Formats ISO date string into "Joined Mon Year" (e.g. "Joined Aug 2026")
 */
export function formatJoinedDate(dateStr?: string | null): string | null {
  if (!dateStr) return null
  try {
    const date = new Date(dateStr)
    if (Number.isNaN(date.getTime())) return null
    return `Joined ${date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
  } catch {
    return null
  }
}
